import { styled } from '@linaria/react';
import { Link } from 'react-router';
import { FLORA_SETTINGS_NAV_GROUPS, FLORA_SETTINGS_PATHS } from './agent-settings.constants';
import type { FloraSettingsSection } from './agent-settings.type';
import { SettingsSectionIcon } from './settings-section-icon';

interface AgentSettingsNavProps {
  /** The section being shown; a connection's detail page counts as Connections. */
  activeSection: FloraSettingsSection;
}

/**
 * Renders the Flora settings left menu: grouped links to each section, each
 * with its coloured icon tile, and the current one marked `aria-current="page"`
 * and an indigo bar.
 *
 * @param {AgentSettingsNavProps} props - The section being shown
 * @returns {JSX.Element} The settings menu
 */
export function AgentSettingsNav({ activeSection }: AgentSettingsNavProps) {
  return (
    <Nav aria-label="Flora settings sections">
      {FLORA_SETTINGS_NAV_GROUPS.map((group) => (
        <Group key={group.label}>
          <GroupLabel>{group.label}</GroupLabel>
          {group.items.map((item) => {
            const isActive = item.section === activeSection;
            return (
              <NavItem
                key={item.section}
                to={FLORA_SETTINGS_PATHS[item.section]}
                aria-current={isActive ? 'page' : undefined}
                data-active={isActive || undefined}
              >
                <SettingsSectionIcon section={item.section} size="sm" />
                {item.label}
              </NavItem>
            );
          })}
        </Group>
      ))}
    </Nav>
  );
}

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1 1 220px;
  max-width: 260px;
  padding: 32px 16px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    max-width: none;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px 16px;
    padding: 12px 16px;
    border-right: none;
    border-bottom: 1px solid var(--sky-light);
  }
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  @media (max-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`;

const GroupLabel = styled.div`
  padding: 4px 10px;
  font-size: var(--font-size-xsmall);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-light);

  @media (max-width: 768px) {
    display: none;
  }
`;

// Active state comes from `data-active` (not the URL match) so a connection's
// detail page still highlights Connections.
const NavItem = styled(Link)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 44px;
  padding: 0 10px 0 12px;
  border-radius: 10px;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--ink-dark);
  text-decoration: none;

  &:hover {
    background: var(--sky-lighter);
    color: var(--ink-darkest);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }

  &[data-active] {
    background: var(--secondary-lightest);
    color: var(--secondary-darkest);
    font-weight: var(--font-weight-semibold);
  }

  &[data-active]::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    bottom: 10px;
    width: 3px;
    border-radius: 3px;
    background: var(--secondary-base);
  }
`;

export type { AgentSettingsNavProps };
